import React, { useState } from "react";
import dayjs from "dayjs";
import classNames from "classnames";
import { ChevronLeft, ChevronRight } from "lucide-react";

const cycleData = {
  "2025-07-01": "post",
  "2025-07-02": "post",
  "2025-07-03": "ovulation",
  "2025-07-04": "ovulation",
  "2025-07-05": "ovulation",
  "2025-07-06": "ovulation",
  "2025-07-07": "ovulation",
  "2025-07-08": "pre",
  "2025-07-09": "pre",
  "2025-07-10": "pre",
  "2025-07-11": "pre",
  "2025-07-12": "pre",
  "2025-07-13": "pre",
  "2025-07-14": "pre",
  "2025-07-15": "pre",
  "2025-07-16": "pre",
  "2025-07-17": "pre",
  "2025-07-18": "pre",
  "2025-07-19": "period",
  "2025-07-20": "period",
  "2025-07-21": "period",
  "2025-07-22": "period",
  "2025-07-23": "period",
  "2025-07-24": "period",
  "2025-07-25": "post",
  "2025-07-26": "post",
  "2025-07-27": "post",
  "2025-07-28": "post",
  "2025-07-29": "post",
  "2025-07-30": "post",
  "2025-07-31": "ovulation",
};

const getColorClass = (type: string) => {
  switch (type) {
    case "pre":
      return "bg-yellow-200 text-yellow-800";
    case "period":
      return "bg-pink-200 text-pink-800";
    case "post":
      return "bg-purple-200 text-purple-800";
    case "ovulation":
      return "bg-green-200 text-green-800";
    default:
      return "";
  }
};

const MenstrualCalendar: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const todayStr = dayjs().format("YYYY-MM-DD");
  const year = currentDate.year();
  const month = currentDate.month();
  const startOfMonth = currentDate.startOf("month");
  const daysInMonth = currentDate.daysInMonth();
  const startDay = startOfMonth.day();

  const goToPreviousMonth = () => {
    setCurrentDate(currentDate.subtract(1, "month"));
  };

  const goToNextMonth = () => {
    setCurrentDate(currentDate.add(1, "month"));
  };

  const days = [];
  for (let i = 0; i < startDay; i++) days.push(null);

  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = dayjs(new Date(year, month, d)).format("YYYY-MM-DD");
    days.push({
      day: d,
      type: cycleData[dateStr as keyof typeof cycleData],
      isToday: dateStr === todayStr,
    });
  }

  return (
    <div className="max-w-lg mx-auto p-4 font-sans">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={goToPreviousMonth}
          className="group w-8 h-8 inline-flex items-center justify-center rounded-full border border-secondary hover:bg-secondary hover:text-white"
        >
          <ChevronLeft className="w-4 h-4 text-secondary group-hover:text-white" />
        </button>
        <h2 className="text-center text-lg font-medium">
          {currentDate.format("MMMM YYYY")}
        </h2>
        <button
          onClick={goToNextMonth}
          className="group w-8 h-8 inline-flex items-center justify-center rounded-full border border-secondary hover:bg-secondary hover:text-white"
        >
          <ChevronRight className="w-4 h-4 text-secondary group-hover:text-white" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-2 text-center text-sm text-gray-500 mb-2">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, idx) => (
          <div key={idx}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {days.map((item, idx) =>
          item ? (
            <button
              type="button"
              key={idx}
              className={classNames(
                "w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium",
                getColorClass(item.type),
                item.isToday && "border border-teal-500"
              )}
            >
              {item.day}
            </button>
          ) : (
            <div key={idx} />
          )
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mt-6 bg-white rounded-xl p-4">
        <div className="col-span-1">
          <div className="flex items-center gap-2 text-xs">
            <span className="w-3 h-3 bg-yellow-300 rounded-full inline-block" />{" "}
            <span className="text-xs text-gray-600">Pre-Period</span>
          </div>
        </div>
        <div className="col-span-1">
          <div className="flex items-center gap-2 text-xs">
            <span className="w-3 h-3 bg-pink-300 rounded-full inline-block" />{" "}
            <span className="text-xs text-gray-600">Period Days</span>
          </div>
        </div>
        <div className="col-span-1">
          <div className="flex items-center gap-2 text-xs">
            <span className="w-3 h-3 bg-purple-300 rounded-full inline-block" />{" "}
            <span className="text-xs text-gray-600">Post Period</span>
          </div>
        </div>
        <div className="col-span-1">
          <div className="flex items-center gap-2 text-xs">
            <span className="w-3 h-3 bg-green-300 rounded-full inline-block" />{" "}
            <span className="text-xs text-gray-600">Peak Ovulation</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenstrualCalendar;
